import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import { Trash2 } from 'lucide-react';

export const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MINUTES_PER_DAY = 24 * 60;
const SNAP_MINUTES = 15;
const LATEST_END_MINUTES = MINUTES_PER_DAY - SNAP_MINUTES;
const HOUR_HEIGHT = 48;
const MINUTE_HEIGHT = HOUR_HEIGHT / 60;
const CALENDAR_HEIGHT = 24 * HOUR_HEIGHT;

const makeId = () => (
  (typeof crypto !== 'undefined' && crypto.randomUUID?.())
  || `schedule-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

export const timeToMinutes = (time) => {
  const [hours, minutes] = String(time || '00:00').split(':').map(Number);
  return (hours * 60) + minutes;
};

export const minutesToTime = (minutes) => {
  const safeMinutes = Math.max(0, Math.min(LATEST_END_MINUTES, minutes));
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

export const snapToQuarterHour = (minutes) => (
  Math.max(0, Math.min(LATEST_END_MINUTES, Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES))
);

const formatTime = (time) => {
  const minutes = timeToMinutes(time);
  const hours = Math.floor(minutes / 60);
  const suffix = hours >= 12 && hours < 24 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${String(minutes % 60).padStart(2, '0')} ${suffix}`;
};

const replaceScheduleDay = (schedules, scheduleId, day, patch, splitId = makeId()) => {
  const schedule = schedules.find((item) => item.id === scheduleId);
  if (!schedule) return schedules;
  if (schedule.days.length === 1) {
    return schedules.map((item) => (item.id === scheduleId ? { ...item, ...patch } : item));
  }
  return [
    ...schedules.map((item) => (
      item.id === scheduleId
        ? { ...item, days: item.days.filter((scheduleDay) => scheduleDay !== day) }
        : item
    )),
    { ...schedule, ...patch, id: splitId, days: [day] },
  ];
};

const removeScheduleDay = (schedules, scheduleId, day) => schedules
  .map((schedule) => (
    schedule.id === scheduleId
      ? { ...schedule, days: schedule.days.filter((scheduleDay) => scheduleDay !== day) }
      : schedule
  ))
  .filter((schedule) => schedule.days.length > 0);

const pointerMinutes = (event, element) => {
  const rect = element.getBoundingClientRect();
  return Math.max(0, Math.min(
    LATEST_END_MINUTES - 1,
    (event.clientY - rect.top) / MINUTE_HEIGHT,
  ));
};

export default function WeeklyScheduleEditor({
  schedules,
  onChange,
  createSchedule,
  getScheduleColor,
  getScheduleLabel,
  backgroundSchedules = [],
  isScheduleReadOnly = () => false,
  getScheduleGroupKey = () => 'schedule-group',
}) {
  const calendarViewportRef = useRef(null);
  const dragRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (calendarViewportRef.current) {
      calendarViewportRef.current.scrollTop = 9 * HOUR_HEIGHT;
    }
  }, []);

  const daySchedules = useMemo(() => WEEK_DAYS.reduce((result, day) => {
    result[day] = [
      ...backgroundSchedules.map((schedule) => ({ schedule, background: true })),
      ...schedules.map((schedule) => ({
        schedule,
        background: isScheduleReadOnly(schedule),
      })),
    ]
      .filter(({ schedule }) => schedule.days.includes(day))
      .sort((left, right) => left.schedule.start.localeCompare(right.schedule.start));
    return result;
  }, {}), [backgroundSchedules, isScheduleReadOnly, schedules]);

  const selectedSchedule = schedules.find((schedule) => (
    schedule.id === selectedBlock?.scheduleId && schedule.days.includes(selectedBlock.day)
  ));

  const overlapsSameGroup = (candidate, ignoredScheduleId = null) => schedules.some((schedule) => (
    schedule.id !== ignoredScheduleId
    && getScheduleGroupKey(schedule) === getScheduleGroupKey(candidate)
    && schedule.days.some((day) => candidate.days.includes(day))
    && timeToMinutes(candidate.start) < timeToMinutes(schedule.end)
    && timeToMinutes(candidate.end) > timeToMinutes(schedule.start)
  ));

  const beginCreate = (event, day) => {
    if (
      !addMode
      || event.button !== 0
      || event.target.closest?.('[data-schedule-block="true"]')
    ) return;
    const slot = Math.min(
      (LATEST_END_MINUTES / SNAP_MINUTES) - 1,
      Math.floor(pointerMinutes(event, event.currentTarget) / SNAP_MINUTES),
    );
    dragRef.current = {
      type: 'create',
      pointerId: event.pointerId,
      day,
      anchorSlot: slot,
      element: event.currentTarget,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraft({ day, start: slot * SNAP_MINUTES, end: (slot + 1) * SNAP_MINUTES });
    setFeedback('');
    event.preventDefault();
  };

  const beginResize = (event, schedule, day, edge) => {
    event.stopPropagation();
    const column = event.currentTarget.closest('[data-day-column="true"]');
    dragRef.current = {
      type: 'resize',
      pointerId: event.pointerId,
      scheduleId: schedule.id,
      day,
      edge,
      start: timeToMinutes(schedule.start),
      end: timeToMinutes(schedule.end),
      element: column,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraft({
      scheduleId: schedule.id,
      day,
      start: timeToMinutes(schedule.start),
      end: timeToMinutes(schedule.end),
    });
    event.preventDefault();
  };

  const updateDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const minutes = pointerMinutes(event, drag.element);
    if (drag.type === 'create') {
      const slot = Math.min(
        (LATEST_END_MINUTES / SNAP_MINUTES) - 1,
        Math.floor(minutes / SNAP_MINUTES),
      );
      setDraft({
        day: drag.day,
        start: Math.min(drag.anchorSlot, slot) * SNAP_MINUTES,
        end: (Math.max(drag.anchorSlot, slot) + 1) * SNAP_MINUTES,
      });
      return;
    }

    const snapped = snapToQuarterHour(minutes);
    setDraft({
      scheduleId: drag.scheduleId,
      day: drag.day,
      start: drag.edge === 'start'
        ? Math.min(snapped, drag.end - SNAP_MINUTES)
        : drag.start,
      end: drag.edge === 'end'
        ? Math.max(snapped, drag.start + SNAP_MINUTES)
        : drag.end,
    });
  };

  const finishDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !draft) return;
    if (drag.type === 'create') {
      const created = createSchedule({
        id: makeId(),
        day: draft.day,
        start: minutesToTime(draft.start),
        end: minutesToTime(draft.end),
      });
      if (overlapsSameGroup(created)) {
        setFeedback('That time overlaps another block for this category.');
      } else {
        onChange([...schedules, created]);
        setSelectedBlock({ scheduleId: created.id, day: draft.day });
        setAddMode(false);
        setFeedback('');
      }
    } else {
      const original = schedules.find((schedule) => schedule.id === drag.scheduleId);
      const candidate = {
        ...original,
        days: [drag.day],
        start: minutesToTime(draft.start),
        end: minutesToTime(draft.end),
      };
      if (overlapsSameGroup(candidate, drag.scheduleId)) {
        setFeedback('That time overlaps another block for this category.');
      } else {
        const splitId = original.days.length > 1 ? makeId() : original.id;
        onChange(replaceScheduleDay(schedules, drag.scheduleId, drag.day, {
          start: candidate.start,
          end: candidate.end,
        }, splitId));
        setSelectedBlock({ scheduleId: splitId, day: drag.day });
        setFeedback('');
      }
    }
    dragRef.current = null;
    setDraft(null);
  };

  const cancelDrag = () => {
    dragRef.current = null;
    setDraft(null);
  };

  const displayRange = (schedule, day) => (
    draft?.scheduleId === schedule.id && draft.day === day
      ? draft
      : { start: timeToMinutes(schedule.start), end: timeToMinutes(schedule.end) }
  );

  const updateSelectedTime = (field, value) => {
    if (!selectedSchedule || !/^\d{2}:\d{2}$/.test(value)) return;
    const snappedValue = minutesToTime(snapToQuarterHour(timeToMinutes(value)));
    const candidate = {
      ...selectedSchedule,
      days: [selectedBlock.day],
      [field]: snappedValue,
    };
    if (candidate.end <= candidate.start) {
      setFeedback('The end time must be after the start time.');
      return;
    }
    if (overlapsSameGroup(candidate, selectedSchedule.id)) {
      setFeedback('That time overlaps another block for this category.');
      return;
    }
    const splitId = selectedSchedule.days.length > 1 ? makeId() : selectedSchedule.id;
    onChange(replaceScheduleDay(
      schedules,
      selectedSchedule.id,
      selectedBlock.day,
      { [field]: snappedValue },
      splitId,
    ));
    setSelectedBlock({ scheduleId: splitId, day: selectedBlock.day });
    setFeedback('');
  };

  const deleteSelected = () => {
    if (!selectedSchedule) return;
    onChange(removeScheduleDay(schedules, selectedSchedule.id, selectedBlock.day));
    setSelectedBlock(null);
    setFeedback('');
  };

  return (
    <Stack spacing={1.25}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="body2" sx={{ color: 'primary.main' }}>
          {addMode
            ? 'Drag within one day to place the new time block.'
            : 'Swipe to move around the calendar. Tap one of your blocks to edit it.'}
        </Typography>
        <Button
          type="button"
          variant={addMode ? 'outlined' : 'contained'}
          color={addMode ? 'secondary' : 'primary'}
          onClick={() => {
            setAddMode((current) => !current);
            setDraft(null);
            dragRef.current = null;
            setFeedback('');
          }}
          sx={(theme) => ({
            m: 0,
            flexShrink: 0,
            color: addMode ? theme.palette.secondary.main : theme.palette.background.default,
            boxShadow: addMode
              ? 'none'
              : `0 4px 0 ${darken(theme.palette.primary.main, 0.55)}, 0 0 12px ${alpha(theme.palette.primary.main, 0.32)}`,
          })}
        >
          {addMode ? 'Cancel adding' : 'Add time block'}
        </Button>
      </Stack>
      {feedback && (
        <Typography role="alert" variant="body2" sx={{ color: 'secondary.main' }}>
          {feedback}
        </Typography>
      )}
      <Box
        ref={calendarViewportRef}
        data-testid="weekly-schedule-viewport"
        sx={(theme) => ({
          maxHeight: { xs: 430, sm: 520 },
          overflow: 'auto',
          border: `2px solid ${alpha(theme.palette.primary.main, 0.68)}`,
          borderRadius: 3,
          backgroundColor: theme.palette.background.default,
          boxShadow: `inset 0 0 24px rgba(0, 0, 0, 0.5), 0 0 18px ${alpha(theme.palette.primary.main, 0.14)}`,
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.secondary.main} ${theme.palette.background.default}`,
        })}
      >
        <Box sx={{ minWidth: 850 }}>
          <Box
            sx={(theme) => ({
              position: 'sticky',
              top: 0,
              zIndex: 5,
              display: 'grid',
              gridTemplateColumns: '66px repeat(7, minmax(108px, 1fr))',
              gap: '2px',
              p: '2px',
              backgroundColor: theme.palette.background.default,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              boxShadow: `0 5px 12px rgba(0, 0, 0, 0.55)`,
            })}
          >
            <Box
              sx={(theme) => ({
                position: 'sticky',
                left: 0,
                zIndex: 7,
                backgroundColor: theme.palette.background.default,
              })}
            />
            {WEEK_DAYS.map((day) => (
              <Typography
                key={day}
                variant="caption"
                sx={(theme) => ({
                  py: 1.25,
                  textAlign: 'center',
                  color: theme.palette.primary.main,
                  backgroundColor: lighten(theme.palette.background.paper, 0.06),
                  borderRadius: 1.5,
                })}
              >
                {day}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '66px repeat(7, minmax(108px, 1fr))',
              gap: '2px',
              p: '2px',
            }}
          >
            <Box
              aria-hidden="true"
              sx={(theme) => ({
                position: 'sticky',
                left: 0,
                zIndex: 4,
                height: CALENDAR_HEIGHT,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `5px 0 10px rgba(0, 0, 0, 0.5)`,
              })}
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <Typography
                  key={hour}
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: hour * HOUR_HEIGHT,
                    right: 8,
                    transform: 'translateY(-1px)',
                    color: 'text.secondary',
                    fontSize: '0.58rem',
                  }}
                >
                  {formatTime(`${String(hour).padStart(2, '0')}:00`)}
                </Typography>
              ))}
            </Box>

            {WEEK_DAYS.map((day) => (
              <Box
                key={day}
                role="gridcell"
                aria-label={`${day} schedule`}
                data-day-column="true"
                onPointerDown={(event) => beginCreate(event, day)}
                onPointerMove={updateDrag}
                onPointerUp={finishDrag}
                onPointerCancel={cancelDrag}
                sx={(theme) => ({
                  position: 'relative',
                  height: CALENDAR_HEIGHT,
                  touchAction: addMode ? 'none' : 'pan-x pan-y',
                  cursor: addMode ? 'crosshair' : 'grab',
                  userSelect: 'none',
                  background: `repeating-linear-gradient(
                    to bottom,
                    ${lighten(theme.palette.background.paper, 0.045)} 0,
                    ${lighten(theme.palette.background.paper, 0.045)} ${HOUR_HEIGHT}px,
                    ${theme.palette.background.paper} ${HOUR_HEIGHT}px,
                    ${theme.palette.background.paper} ${HOUR_HEIGHT * 2}px
                  )`,
                  borderRadius: 1,
                })}
              >
                {daySchedules[day].map(({ schedule, background }) => {
                  const range = displayRange(schedule, day);
                  const color = getScheduleColor?.(schedule) || '#00E5FF';
                  const isSelected = !background
                    && selectedBlock?.scheduleId === schedule.id
                    && selectedBlock.day === day;
                  return (
                    <Box
                      key={`${schedule.id}-${day}`}
                      data-schedule-block="true"
                      onClick={() => {
                        if (!background) {
                          setSelectedBlock({ scheduleId: schedule.id, day });
                          setFeedback('');
                        }
                      }}
                      sx={(theme) => ({
                        position: 'absolute',
                        zIndex: 2,
                        top: range.start * MINUTE_HEIGHT,
                        left: 4,
                        right: 4,
                        height: Math.max(12, (range.end - range.start) * MINUTE_HEIGHT),
                        minHeight: 12,
                        px: 0.75,
                        py: 0.5,
                        overflow: 'hidden',
                        color: theme.palette.primary.main,
                        opacity: background ? 0.32 : 1,
                        pointerEvents: background ? 'none' : 'auto',
                        border: `${isSelected ? 3 : 2}px solid ${color}`,
                        borderRadius: 1.5,
                        background: `linear-gradient(135deg, ${darken(color, 0.58)}, ${darken(color, 0.76)})`,
                        boxShadow: isSelected
                          ? `0 0 0 2px ${theme.palette.green?.main || '#39FF14'}, 0 0 16px ${alpha(color, 0.55)}`
                          : `0 0 12px ${alpha(color, 0.42)}`,
                        cursor: background ? 'inherit' : 'pointer',
                      })}
                    >
                      {!background && (
                        <Box
                          role="slider"
                          aria-label={`Change ${day} start time`}
                          aria-valuemin={0}
                          aria-valuemax={range.end - SNAP_MINUTES}
                          aria-valuenow={range.start}
                          onPointerDown={(event) => beginResize(event, schedule, day, 'start')}
                          onPointerMove={updateDrag}
                          onPointerUp={finishDrag}
                          onPointerCancel={cancelDrag}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 10,
                            cursor: 'ns-resize',
                            touchAction: 'none',
                            bgcolor: alpha(color, 0.75),
                          }}
                        />
                      )}
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
                        <Box sx={{ minWidth: 0, pt: 0.35 }}>
                          {getScheduleLabel && (
                            <Typography variant="caption" sx={{ color: 'primary.main', display: 'block' }} noWrap>
                              {getScheduleLabel(schedule)}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.55rem' }} noWrap>
                            {formatTime(minutesToTime(range.start))}–{formatTime(minutesToTime(range.end))}
                          </Typography>
                        </Box>
                        {!background && (
                          <IconButton
                            size="small"
                            aria-label={`Delete ${day} ${schedule.start} schedule`}
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                              event.stopPropagation();
                              onChange(removeScheduleDay(schedules, schedule.id, day));
                              if (isSelected) setSelectedBlock(null);
                            }}
                            sx={{
                              mt: '2px !important',
                              p: 0.25,
                              color: 'primary.main',
                              border: 0,
                              bgcolor: 'rgba(0, 0, 0, 0.42)',
                              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.72)' },
                            }}
                          >
                            <Trash2 size={13} />
                          </IconButton>
                        )}
                      </Stack>
                      {!background && (
                        <Box
                          role="slider"
                          aria-label={`Change ${day} end time`}
                          aria-valuemin={range.start + SNAP_MINUTES}
                          aria-valuemax={LATEST_END_MINUTES}
                          aria-valuenow={range.end}
                          onPointerDown={(event) => beginResize(event, schedule, day, 'end')}
                          onPointerMove={updateDrag}
                          onPointerUp={finishDrag}
                          onPointerCancel={cancelDrag}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 10,
                            cursor: 'ns-resize',
                            touchAction: 'none',
                            bgcolor: alpha(color, 0.75),
                          }}
                        />
                      )}
                    </Box>
                  );
                })}

                {draft?.day === day && !draft.scheduleId && (
                  <Box
                    sx={(theme) => ({
                      position: 'absolute',
                      zIndex: 3,
                      top: draft.start * MINUTE_HEIGHT,
                      left: 4,
                      right: 4,
                      height: (draft.end - draft.start) * MINUTE_HEIGHT,
                      minHeight: 12,
                      border: `2px solid ${theme.palette.secondary.main}`,
                      borderRadius: 1.5,
                      backgroundColor: darken(theme.palette.secondary.main, 0.68),
                      boxShadow: `0 0 16px ${alpha(theme.palette.secondary.main, 0.55)}`,
                      pointerEvents: 'none',
                    })}
                  >
                    <Typography variant="caption" sx={{ px: 0.5, color: 'secondary.main', fontSize: '0.55rem' }}>
                      {formatTime(minutesToTime(draft.start))}–{formatTime(minutesToTime(draft.end))}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {selectedSchedule && (
        <Box
          sx={(theme) => ({
            p: 1.5,
            border: `2px solid ${getScheduleColor?.(selectedSchedule) || theme.palette.primary.main}`,
            borderRadius: 3,
            backgroundColor: lighten(theme.palette.background.paper, 0.06),
            boxShadow: `0 0 14px ${alpha(getScheduleColor?.(selectedSchedule) || theme.palette.primary.main, 0.25)}`,
          })}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
          >
            <Box sx={{ minWidth: { sm: 120 } }}>
              <Typography variant="subtitle2">{selectedBlock.day}</Typography>
              {getScheduleLabel && (
                <Typography variant="caption" sx={{ color: 'primary.main' }}>
                  {getScheduleLabel(selectedSchedule)}
                </Typography>
              )}
            </Box>
            <TextField
              label="Start"
              type="time"
              size="small"
              value={selectedSchedule.start}
              onChange={(event) => updateSelectedTime('start', event.target.value)}
              inputProps={{ step: 900 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End"
              type="time"
              size="small"
              value={selectedSchedule.end}
              onChange={(event) => updateSelectedTime('end', event.target.value)}
              inputProps={{ step: 900 }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              type="button"
              color="secondary"
              onClick={deleteSelected}
              startIcon={<Trash2 size={16} />}
              sx={{ m: 0, ml: { sm: 'auto !important' } }}
            >
              Delete block
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
